import { endOfRange, rangeDurationMinutes } from "@/lib/dateSplit";

export type TaskTimingValue = {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  durationMinutes: string;
};

export type TaskScheduleValue = {
  timing: TaskTimingValue;
  alertEnabled: boolean;
};

export function createTaskScheduleValue({
  date = "",
  startTime = "",
  durationMinutes = "",
  alertEnabled = false,
}: {
  date?: string;
  startTime?: string;
  durationMinutes?: string;
  alertEnabled?: boolean;
} = {}): TaskScheduleValue {
  return {
    timing: setTaskTimingDuration({
      startDate: date,
      endDate: date,
      startTime,
      endTime: "",
      durationMinutes: "",
    }, durationMinutes),
    alertEnabled,
  };
}

export type TaskStatusValue = {
  status: "pending" | "done" | "skipped";
  startedAt: Date | null;
  completedAt: Date | null;
  timing: TaskTimingValue;
};

export function setTaskTimingStart(value: TaskTimingValue, startDate: string, startTime: string): TaskTimingValue {
  const duration = Number.parseInt(value.durationMinutes, 10);
  if (startTime && Number.isFinite(duration)) {
    const end = endOfRange(startDate, startTime, Math.max(0, duration));
    return {
      ...value,
      startDate,
      startTime,
      endDate: end?.date ?? startDate,
      endTime: end?.time ?? "",
      durationMinutes: String(Math.max(0, duration)),
    };
  }

  return recalculateTaskTimingDuration({ ...value, startDate, startTime });
}

export function setTaskTimingEnd(value: TaskTimingValue, endDate: string, endTime: string): TaskTimingValue {
  return recalculateTaskTimingDuration({ ...value, endDate, endTime });
}

export function setTaskTimingDuration(value: TaskTimingValue, durationMinutes: string): TaskTimingValue {
  const duration = Number.parseInt(durationMinutes, 10);
  const end = endOfRange(value.startDate, value.startTime, duration);
  return {
    ...value,
    endDate: end?.date ?? value.startDate,
    endTime: end?.time ?? "",
    durationMinutes,
  };
}

export function setTaskTimingStartDate(value: TaskTimingValue, startDate: string, splitDate: boolean): TaskTimingValue {
  return recalculateTaskTimingDuration({
    ...value,
    startDate,
    endDate: splitDate ? value.endDate : startDate,
  });
}

export function setTaskTimingEndDate(value: TaskTimingValue, endDate: string): TaskTimingValue {
  return recalculateTaskTimingDuration({ ...value, endDate });
}

function recalculateTaskTimingDuration(value: TaskTimingValue): TaskTimingValue {
  const duration = rangeDurationMinutes(value.startDate, value.startTime, value.endDate, value.endTime);
  return { ...value, durationMinutes: duration == null ? "" : String(duration) };
}
